<template>
  <v-card>
    <ValidationObserver ref="obs" v-slot="{ invalid }">
      <v-toolbar dark color="secondary">
        <v-toolbar-title>
          Create new task
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <div>
              <user-search v-if="!userId" @user-selected="selectedUser = $event" only-staff />
              <ValidationProvider name="Title" rules="required" v-slot="{ errors, valid }">
                <v-text-field v-model="data.title" label="Task title" :error-messages="errors" :success="valid" />
              </ValidationProvider>
              <ValidationProvider name="description" rules="required" v-slot="{ errors, valid }">
                <v-textarea
                  rows="1"
                  v-model="data.description"
                  label="Task description"
                  :error-messages="errors"
                  :success="valid"
                />
              </ValidationProvider>
              <ValidationProvider name="task type" rules="required" v-slot="{ errors, valid }">
                <v-select
                  v-model="data.taskTypeId"
                  :items="taskTypes"
                  label="Task Type"
                  single-line
                  dense
                  :error-messages="errors"
                  :success="valid"
                ></v-select>
              </ValidationProvider>
            </div>
            <div>
              <span class="mr-2 pt-2">Assignee:</span>
              <v-autocomplete
                v-model="data.assignedTo"
                class="ml-2 pt-1"
                item-text="name"
                item-value="id"
                dense
                filled
                outlined
                small-chips
                solo
                rounded
                :items="staff"
              >
                <template v-slot:selection="data">
                  <v-chip v-bind="data.attrs" :input-value="data.selected">
                    <v-avatar left>
                      <v-img :src="data.item.avatar"></v-img>
                    </v-avatar>
                    {{ data.item.name }}
                  </v-chip>
                </template>
                <template v-slot:item="data">
                  <v-list-item-avatar>
                    <img :src="data.item.avatar" />
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title v-html="data.item.name"></v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </div>
            <div>
              <v-menu ref="menu1" v-model="menu1" transition="scale-transition">
                <template v-slot:activator="{ on, attrs }">
                  <ValidationProvider name="task date" rules="required" v-slot="{ errors, valid }">
                    <v-text-field
                      v-model="data.taskEventDate"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-calendar"
                      clearable
                      outlined
                      rounded
                      dense
                      label="Choose a task date"
                      :error-messages="errors"
                      :success="valid"
                    ></v-text-field>
                  </ValidationProvider>
                </template>
                <v-date-picker v-model="data.taskEventDate"></v-date-picker>
              </v-menu>
            </div>
            <div>
              <v-switch
                v-if="data.taskEventDate"
                v-model="data.sendReminder"
                label="Send Reminder"
              ></v-switch>
              <j-input-mask
                v-if="data.sendReminder"
                v-model="data.reminderTime"
                :value="data.reminderTime"
                :mask="['##:##']"
                masked
                placeholder="Time"
                suffix="HH:MM"
              />
            </div>
            <div v-if="data.taskEventDate && (userId || selectedUser)">
              <v-switch
                v-model="data.isRecurring"
                :label="`Setup task to repeat starting on ${data.taskEventDate} `"
              ></v-switch>
              <div v-if="data.isRecurring">
                <ValidationProvider name="Recurring Frequency" rules="required" v-slot="{ errors, valid }">
                  <v-select
                    v-model="data.recurringFrequency"
                    :items="[
                      { text: 'Daily', value: 'D' },
                      { text: 'Weekly', value: 'W' },
                      { text: 'Monthly', value: 'M' },
                    ]"
                    label="How should this repeat"
                    :error-messages="errors"
                    :success="valid"
                  />
                </ValidationProvider>
                <span class="caption">
                  Please make sure the user and assigned to is correct because all future tasks will use the above
                  details for and repeated tasks
                </span>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn :disabled="invalid || (userId == null && selectedUser == null)" @click="createTask()">
          Save
        </v-btn>
      </v-card-actions>
    </ValidationObserver>
  </v-card>
</template>

<script>
import { TASK_TYPE_QUERY } from "@/graphql/queries/task";
import { CREATE_TASK_EVENT_MUTATION } from "@/graphql/mutations/task";
import { STAFF_QUERY } from "@/graphql/queries/user";
import { decodeId } from "@/lib/string";
import { getUserName } from "@/lib/username";
import { fancyShortDate } from "@/lib/fancy_date";
import UserSearch from "@/components/client/blocks/UserSearch";
import JInputMask from "@/components/shared/inputs/JInputMask";

export default {
  name: "TaskForm",
  props: {
    userId: {
      type: String,
      required: false,
    },
  },
  components: {
    UserSearch,
    JInputMask
  },
  data() {
    return {
      taskEvents: [],
      staff: [],
      menu1: false,
      taskTypes: [],
      selectedUser: null,
      data: {
        assignedTo: null,
        title: null,
        description: null,
        taskEventDate: null,
        taskTypeId: null,
        recurringFrequency: null,
        isRecurring: null,
        sendReminder: null,
        reminderTime: null
      },
    };
  },
  computed: {},
  methods: {
    close() {
      this.data = {
        assignedTo: null,
        title: null,
        description: null,
        taskEventDate: null,
        taskTypeId: null,
      }

      this.$emit("close");

    },
    createTask() {
      this.data.userId = this.userId ? this.userId : this.selectedUser;
      this.saving = true;
      this.$apollo
        .mutate({
          mutation: CREATE_TASK_EVENT_MUTATION,
          variables: {
            data: this.data,
          },
          refetchQueries: ["taskEventsQuery", "myUsersQuery"],
        })
        .then((result) => {
          this.saving = false;
          if (!result.data.createTaskEvent.created) {
            alert(result.data.createTaskEvent.error);
          } else {
            this.$toasted.success("Task Created", {
              icon: "arm-flex",
              duration: 2000,
              position: "bottom-center",
            });
            this.$emit("created");
            this.close();
          }
        });
    },
  },
  apollo: {
    taskTypes: {
      query: TASK_TYPE_QUERY,
      fetchPolicy: "cache-and-network",
      update(data) {
        return data.taskTypes.map((item) => {
          return {
            text: item.title,
            value: item.id,
          };
        });
      },
    },
    users: {
      query: STAFF_QUERY,
      variables() {
        return {
          isActive: true,
          isStaff: true,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data && !response.loading) {
          this.staff = [];

          response.data.users.edges.forEach((item) => {
            let data = {
              id: decodeId(item.node.id),
              name: getUserName(item.node),
              avatar: item.node.userProfile?.profilePicture,
            };

            this.staff.push(data);
          });

          this.staff.sort((a, b) => (a.text > b.text ? 1 : -1));
        }
      },
    },
  },
};
</script>
